/**
 * W-8ECI Service
 * Following the same clean architecture pattern as W8BEN service
 */

import { W8ECIFormModel, W8ECIResponseModel, W8ECIFieldMapper } from "../Models/W8ECIModels";
import { BASE_URL } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";
import { updateFileData } from "./fileDataService";

export class W8ECIService {
  private static readonly API_ENDPOINT = `${BASE_URL}/FileData/ImportFiles`;

  /**
   * Create form model from API response data
   */
  static fromApiResponse(apiData: any): W8ECIFormModel {
    // Create response model from API data
    const responseModel = new W8ECIResponseModel(apiData);

    // Map legacy API field names
    this.mapLegacyApiFields(apiData, responseModel);

    // Use field mapper to convert to form model
    return W8ECIFieldMapper.mapResponseToForm(responseModel);
  }

  /**
   * Map legacy API field names to current field names
   */
  private static mapLegacyApiFields(apiData: any, responseModel: W8ECIResponseModel): void {
    const legacyMappings: Record<string, string[]> = {
      "Beneficial Owner name": [
        "Organization_name",
        "organization_name",
        "Name",
        "name",
        "FirstName LastName",
        "firstName lastName",
      ],
      "Country of incorporation or organization": [
        "Country_of_incorporation",
        "incorporation_country",
        "Country_of_incorporation_organization",
      ],
      "Permanent residence address": [
        "Permanent_residence_address",
        "permanent_address",
        "Permanent residence address",
        "permanent_street",
      ],
      "Permanent city address": [
        "Permanent_city_address",
        "permanent_city",
        "Permanent_address_city_state_zip",
      ],
      "Permanent address country": ["Permanent_address_country", "permanent_country"],
      "U.S. taxpayer identification number": [
        "US_taxpayer_identification_number",
        "us_tin",
        "U.S._taxpayer_identification_number",
      ],
      "Foreign tax identifying number": [
        "Foreign_tax_identifying_number",
        "foreign_tin",
        "Foreign_tax_identifying_number",
      ],
      Signature_date: ["Signature_date", "signature_date", "sign_date"],
      signature_date: ["signature_date", "Signature_date", "sign_date"], // Form field variant
      Print_name: ["Print_name", "printed_name", "signer_name", "Signer_printed_name"],
      "Date of birth": ["Date_of_birth", "date_of_birth", "Date of birth"],
      "ECI-Business_address_street": ["ECI-Business_address_street", "Business_address_street"],
      "ECI-Business_address_city_state_zip": [
        "ECI-Business_address_city_state_zip",
        "Business_address_city_state_zip",
      ],
      Signing_capacity: ["Signing_capacity", "signing_capacity", "Signature_capacity"],
      certify: ["certify", "certification", "is_certified"],
      isSigned: ["isSigned", "is_signed", "signed"],
      isModified: ["isModified", "is_modified", "modified"],
      "Check here to certify that you are a dealer in securities": [
        "Check here to certify that you are a dealer in securities",
        "securities_dealer",
        "dealer_certification",
      ],
    };

    Object.entries(legacyMappings).forEach(([modelField, legacyFields]) => {
      if (!responseModel[modelField]) {
        for (const legacyField of legacyFields) {
          if (apiData[legacyField]) {
            responseModel[modelField] = apiData[legacyField];
            break;
          }
        }
      }
    });
  }

  /**
   * Convert form model to API payload format
   */
  static toApiPayload(model: W8ECIFormModel): Record<string, any> {
    const payload: Record<string, any> = {};

    // Map all model fields to API payload
    Object.keys(model).forEach((field) => {
      const value = (model as any)[field];
      if (value !== undefined && typeof value !== "function") {
        payload[field] = value;
      }
    });

    return payload;
  }

  /**
   * Save form data to API
   */
  static async saveToApi(
    model: W8ECIFormModel,
    row: any,
    selectedIndex: number = 0
  ): Promise<boolean> {
    try {
      // Enhanced authentication with multiple fallbacks
      let userData;
      let token;

      try {
        userData = getUserIdFromLocalStorage();
        token = userData?.token;
      } catch (error) {
        console.log("W8ECI Auth - getUserIdFromLocalStorage failed:", error);
      }

      // Fallback authentication methods
      if (!token) {
        try {
          const storedUserData = localStorage.getItem("userData");
          if (storedUserData) {
            const parsed = JSON.parse(storedUserData);
            token = parsed.token;
          }
        } catch (error) {
          console.log("W8ECI Auth - fallback userData parsing failed:", error);
        }
      }

      if (!token) {
        try {
          token = localStorage.getItem("token");
        } catch (error) {
          console.log("W8ECI Auth - direct token retrieval failed:", error);
        }
      }

      if (!token) {
        console.error("W8ECI Auth - No authentication token found after all fallbacks");
        throw new Error("No authentication token found");
      }

      // Convert model to API payload
      const formData = this.toApiPayload(model);

      let parsedFormsData;
      try {
        // Parse formsData and check if it's complex structure or simple array
        parsedFormsData = JSON.parse(row.formsData || "[]");

        // Check if it's the complex structure with formsData property and withholdingData
        if (
          parsedFormsData &&
          typeof parsedFormsData === "object" &&
          parsedFormsData.formsData &&
          Array.isArray(parsedFormsData.formsData)
        ) {
          console.log(
            "🔍 W8ECI: Detected complex formsData structure - preserving withholdingData"
          );

          // Find and update the W8ECI form in the complex structure
          let formUpdated = false;

          for (let i = 0; i < parsedFormsData.formsData.length; i++) {
            const formItem = parsedFormsData.formsData[i];
            if (formItem && formItem.data && Array.isArray(formItem.data)) {
              for (let j = 0; j < formItem.data.length; j++) {
                const dataItem = formItem.data[j];
                if (
                  dataItem &&
                  (dataItem.formType === "W8ECI" || dataItem.Form_number === "W-8ECI")
                ) {
                  console.log(`✅ W8ECI: Updating form at formsData[${i}].data[${j}]`);
                  parsedFormsData.formsData[i].data[j] = {
                    ...dataItem,
                    ...formData,
                  };
                  formUpdated = true;
                  break;
                }
              }
              if (formUpdated) break;
            }
          }

          if (!formUpdated) {
            console.log("⚠️ W8ECI: Could not find existing form, adding new one");
            parsedFormsData.formsData.push({
              request_id: "new-w8eci-" + Date.now(),
              status: "success",
              data: [formData],
              reportdata: {
                requestId: "new-w8eci-" + Date.now(),
                subRequestId: "new-w8eci-" + Date.now(),
                taxForms: "W8ECI",
                type: "extraction",
              },
            });
          }
        } else {
          // Handle as simple array
          console.log("🔍 W8ECI: Detected simple array structure");
          const formsDataArray = Array.isArray(parsedFormsData) ? parsedFormsData : [];

          // Update the form at the specified index (or add if it doesn't exist)
          const currentItem = formsDataArray[selectedIndex];

          if (selectedIndex < formsDataArray.length && currentItem) {
            // Update existing form data by merging with new data
            formsDataArray[selectedIndex] = {
              ...currentItem,
              ...formData,
            };
          } else {
            // Ensure array is large enough
            while (formsDataArray.length <= selectedIndex) {
              formsDataArray.push({});
            }
            // Add new form data
            formsDataArray[selectedIndex] = formData;
          }

          parsedFormsData = formsDataArray;
        }
      } catch (error) {
        console.log("W8ECI: Error parsing existing formsData, creating new array:", error);
        parsedFormsData = [formData];
      }

      // Store back as stringified structure (preserving complex format if applicable)
      row.formsData = JSON.stringify(parsedFormsData);

      // Send to API using centralized service
      await updateFileData(row);

      console.log("W8ECI Save operation completed successfully");
      return true;
    } catch (error) {
      console.error("Failed to save W-8ECI form:", error);
      throw error;
    }
  }
}
